import type { TFunction } from 'i18next';
import { z } from 'zod';
import type {
  AirlineBaggageRuleItem,
  SaveAirlineBaggageRuleRequest,
} from '@/api/generated/schemas';
import { BaggageCategory } from '@/api/generated/schemas';

/**
 * Kanály nákupu — na drátě tři nezávislé `bool` sloupce, ve formuláři jeden vícehodnotový výběr.
 * Vlastní UI hodnoty, protože backend pro ně žádný enum nemá (viz spec §4.4).
 */
export const BAGGAGE_PURCHASE_CHANNELS = ['AtBooking', 'AfterBooking', 'AtAirport'] as const;

export type BaggagePurchaseChannel = (typeof BAGGAGE_PURCHASE_CHANNELS)[number];

/**
 * „V ceně letenky" vs. „za příplatek" — dvě hodnoty jednoho přepínače nad `bool` sloupcem
 * `isIncluded`. Přepínač místo zaškrtávátka, protože obě varianty mají vlastní význam a výchozí
 * hodnota (příplatek) je tak vidět.
 */
export const BAGGAGE_PRICING_MODES = ['Extra', 'Included'] as const;

export type BaggagePricingMode = (typeof BAGGAGE_PRICING_MODES)[number];

/** Celé číslo z textového pole; `null` = prázdné nebo necelé číslo. */
function baggageInteger(raw: string): number | null {
  const value = Number(raw.trim());
  return raw.trim() !== '' && Number.isInteger(value) ? value : null;
}

/** Peněžní částka z textového pole; `null` = prázdné nebo nečíselné. */
function baggageAmount(raw: string): number | null {
  const value = Number(raw.trim());
  return raw.trim() !== '' && Number.isFinite(value) ? value : null;
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Factory Zod schématu pravidla zavazadla — UI zrcadlo BE validace
 * (`SaveAirlineBaggageRuleValidator`); hlášky z `t` až za běhu.
 *
 * `hasCarrierCurrency` je **persistovaná** měna dopravce: ceny vlastní měnu nemají, takže bez měny
 * dopravce nedávají smysl. Backend hlídá totéž proti uložené hodnotě, ne proti formuláři.
 */
export function buildBaggageRuleFormSchema(t: TFunction, hasCarrierCurrency: boolean) {
  return z
    .object({
      id: z.string().nullable(),
      // Bez implicitního defaultu — nevybraná kategorie je `null`, ať nejde uložit domněnku.
      category: z.enum(BaggageCategory).nullable(),
      name: z
        .string()
        .trim()
        .min(1, t('baggage.validation.nameRequired'))
        .max(100, t('baggage.validation.nameMaxLength')),
      description: z.string().max(255, t('baggage.validation.descriptionMaxLength')),
      // Čísla jsou textová pole (jako ostatní číselné vstupy formulářů) — validují se v superRefine.
      widthCm: z.string(),
      heightCm: z.string(),
      depthCm: z.string(),
      weightKg: z.string(),
      pricing: z.enum(BAGGAGE_PRICING_MODES),
      maxPiecesPerPassenger: z.string(),
      purchaseChannels: z.array(z.enum(BAGGAGE_PURCHASE_CHANNELS)),
      priceFrom: z.string(),
      priceTo: z.string(),
    })
    .superRefine((values, ctx) => {
      if (values.category === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['category'],
          message: t('baggage.validation.categoryRequired'),
        });
      }

      // Prázdný rozměr znamená „dle typu"; vyplněný musí být celé kladné číslo.
      for (const path of ['widthCm', 'heightCm', 'depthCm', 'weightKg'] as const) {
        const raw = values[path];
        if (raw.trim() !== '' && (baggageInteger(raw) ?? 0) <= 0) {
          ctx.addIssue({
            code: 'custom',
            path: [path],
            message: t('baggage.validation.positiveNumber'),
          });
        }
      }

      if ((baggageInteger(values.maxPiecesPerPassenger) ?? 0) < 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['maxPiecesPerPassenger'],
          message: t('baggage.validation.maxPiecesMin'),
        });
      }

      if (values.purchaseChannels.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['purchaseChannels'],
          message: t('baggage.validation.purchaseChannelRequired'),
        });
      }

      // Pravidlo v ceně letenky cenu nemá — pole se ani nezobrazují a server je normalizuje na null.
      if (values.pricing === 'Included') {
        return;
      }

      for (const path of ['priceFrom', 'priceTo'] as const) {
        const raw = values[path];
        if (raw.trim() !== '' && (baggageAmount(raw) ?? -1) < 0) {
          ctx.addIssue({
            code: 'custom',
            path: [path],
            message: t('baggage.validation.priceNonNegative'),
          });
        }
      }

      const from = baggageAmount(values.priceFrom);
      const to = baggageAmount(values.priceTo);
      if (from !== null && to !== null && from > to) {
        ctx.addIssue({
          code: 'custom',
          path: ['priceFrom'],
          message: t('baggage.validation.priceOrder'),
        });
      }

      if (!hasCarrierCurrency && (from !== null || to !== null)) {
        ctx.addIssue({
          code: 'custom',
          path: ['priceFrom'],
          message: t('baggage.validation.currencyRequired'),
        });
      }
    });
}

export type BaggageRuleFormValues = z.infer<ReturnType<typeof buildBaggageRuleFormSchema>>;

export function emptyBaggageRuleFormValues(): BaggageRuleFormValues {
  return {
    id: null,
    category: null,
    name: '',
    description: '',
    widthCm: '',
    heightCm: '',
    depthCm: '',
    weightKg: '',
    // Jediné dva business defaulty pravidla — příplatek a jeden kus na osobu (spec §4.3).
    pricing: 'Extra',
    maxPiecesPerPassenger: '1',
    purchaseChannels: [],
    priceFrom: '',
    priceTo: '',
  };
}

/**
 * Tři `bool` pole API → jeden vícehodnotový výběr (pořadí drží `BAGGAGE_PURCHASE_CHANNELS`).
 * Sdílené s tabulkou pravidel, aby se pořadí ani množina kanálů nerozešly mezi formulářem a výpisem.
 */
export function baggagePurchaseChannelsOf(item: AirlineBaggageRuleItem): BaggagePurchaseChannel[] {
  const selected: BaggagePurchaseChannel[] = [];
  if (item.canPurchaseAtBooking) selected.push('AtBooking');
  if (item.canPurchaseAfterBooking) selected.push('AfterBooking');
  if (item.canPurchaseAtAirport) selected.push('AtAirport');
  return selected;
}

/** Mapuje uložené pravidlo na hodnoty formuláře (režim úpravy). */
export function fromAirlineBaggageRuleItem(item: AirlineBaggageRuleItem): BaggageRuleFormValues {
  return {
    id: item.id,
    category: item.category,
    name: item.name,
    description: item.description ?? '',
    widthCm: item.widthCm?.toString() ?? '',
    heightCm: item.heightCm?.toString() ?? '',
    depthCm: item.depthCm?.toString() ?? '',
    weightKg: item.weightKg?.toString() ?? '',
    pricing: item.isIncluded ? 'Included' : 'Extra',
    maxPiecesPerPassenger: item.maxPiecesPerPassenger.toString(),
    purchaseChannels: baggagePurchaseChannelsOf(item),
    priceFrom: item.priceFrom?.toString() ?? '',
    priceTo: item.priceTo?.toString() ?? '',
  };
}

/** Sestaví tělo požadavku; `satisfies` hlídá shodu s BE kontraktem. */
export function toBaggageRuleRequestBody(
  values: BaggageRuleFormValues
): SaveAirlineBaggageRuleRequest {
  const isIncluded = values.pricing === 'Included';
  return {
    id: values.id,
    category: values.category,
    name: values.name.trim(),
    description: emptyToNull(values.description),
    widthCm: baggageInteger(values.widthCm),
    heightCm: baggageInteger(values.heightCm),
    depthCm: baggageInteger(values.depthCm),
    weightKg: baggageInteger(values.weightKg),
    isIncluded,
    // Nečíselnou hodnotu zachytí schéma dřív; `?? 1` jen drží typ non-null a kopíruje DB default.
    maxPiecesPerPassenger: baggageInteger(values.maxPiecesPerPassenger) ?? 1,
    canPurchaseAtBooking: values.purchaseChannels.includes('AtBooking'),
    canPurchaseAfterBooking: values.purchaseChannels.includes('AfterBooking'),
    canPurchaseAtAirport: values.purchaseChannels.includes('AtAirport'),
    // Pravidlo v ceně letenky cenu nemá; server ji normalizuje nezávisle na klientovi.
    priceFrom: isIncluded ? null : baggageAmount(values.priceFrom),
    priceTo: isIncluded ? null : baggageAmount(values.priceTo),
  } satisfies SaveAirlineBaggageRuleRequest;
}
